import '../scss/components/_addtenantmodal.scss'
import React, { useState, useRef } from 'react'
import { insert_tenant_with_files, sendQRCode, sendTenantFormLink } from '../apis/services/pgApi'
import QRCode from 'qrcode';

// ─── File Input (unchanged) ──────────────────────────────────────
const FileInput = ({ label, accept, value, onChange, hint, fieldName }) => {
  const ref = useRef()
  const [preview, setPreview] = useState(null)

  const handleFileChange = async (file) => {
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
      onChange(file, fieldName);
    } else {
      setPreview(null);
      onChange(null, fieldName);
    }
  }

  return (
    <div className="atm-file-wrap">
      <label className="atm-label">{label}</label>
      <div className={`atm-file-box ${value ? 'has-file' : ''}`} onClick={() => ref.current.click()}>
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => handleFileChange(e.target.files[0] || null)} />
        {value ? (
          <div className="atm-file-chosen">
            {preview && (
              <img src={preview} alt="Preview" style={{
                width: '40px',
                height: '40px',
                objectFit: 'cover',
                borderRadius: '4px',
                marginRight: '8px'
              }} />
            )}
            <span className="atm-file-name">{value.name}</span>
            <button className="atm-file-clear"
              onClick={e => { e.stopPropagation(); handleFileChange(null) }}>×</button>
          </div>
        ) : (
          <div className="atm-file-empty">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span>{hint || 'Click to upload'}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Initial form state ───────────────────────────────────────────
const BLANK = {
  name: '', age: '', dob: '', gender: '', phone: '', email: '',
  presentAddress: '', permanentAddress: '',
  emergencyName: '', emergencyPhone: '', emergencyRelation: '',
  profession: '', checkInDate: '',
  aadhaarFile: null, photoFile: null, idFile: null,
}

// ─── AddTenantModal ───────────────────────────────────────────────
const AddTenantModal = ({ isOpen, onClose, room, selectedPG, onSubmit, onSuccess }) => {
  const [mode, setMode] = useState(null)
  // Link mode states
  const [linkEmail, setLinkEmail] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [sendingLink, setSendingLink] = useState(false)

  // QR mode states
  const [qrUpiId, setQrUpiId] = useState('')
  const [qrTenantName, setQrTenantName] = useState('')
  const [qrAdvanceAmount, setQrAdvanceAmount] = useState('')
  const [qrSecurityDeposit, setQrSecurityDeposit] = useState('')
  const [qrPhone, setQrPhone] = useState('')
  const [qrEmail, setQrEmail] = useState('')
  const [qrFile, setQrFile] = useState(null)
  const [qrSent, setQrSent] = useState(false)
  const [sendingQR, setSendingQR] = useState(false)
  const [generatedQRData, setGeneratedQRData] = useState(null)
  const [qrImageUrl, setQrImageUrl] = useState('') // For displaying QR
  const [showQRPreview, setShowQRPreview] = useState(false)
  const qrCanvasRef = useRef(null)

  // QR field‑specific errors – updated in real‑time
  const [qrErrors, setQrErrors] = useState({})

  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(null)

  if (!isOpen) return null

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: undefined }))
  }

  const setFile = (file, fieldName) => {
    setForm(p => ({ ...p, [fieldName]: file }));
  }

  const reset = () => {
    setMode(null);
    setLinkEmail(''); setLinkSent(false);
    setQrTenantName('');
    setQrAdvanceAmount('');
    setQrSecurityDeposit('');
    setQrPhone('');
    setQrEmail('');
    setQrImageUrl('');
    setQrUpiId(''); // Default UPI ID
    setQrFile(null);
    setQrSent(false);
    setQrErrors({});
    setForm(BLANK);
    setErrors({});
    setApiError(null)
  }

  const handleClose = () => { reset(); onClose() }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Required'
    if (!form.phone.trim()) e.phone = 'Required'
    if (!form.email.trim()) e.email = 'Email is required'
    if (!form.checkInDate) e.checkInDate = 'Required'
    return e
  }

  const handleSubmit = async () => {
    const validationErrors = validate()
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return
    }

    if (!selectedPG || !selectedPG.PGID) {
      setApiError('No PG selected. Please select a PG from the dashboard.')
      return
    }

    setLoading(true)
    setApiError(null)

    try {
      const formData = new FormData()
      const tenantData = {
        Status: 'NEW',
        PGID: selectedPG.PGID,
        TenantName: form.name,
        Tenantage: form.age ? parseInt(form.age) : null,
        TenantDOB: form.dob || null,
        Gender: form.gender ? form.gender.charAt(0).toUpperCase() + form.gender.slice(1) : null,
        PhoneNumber: form.phone,
        Email1: form.email || null,
        PresentAddress: form.presentAddress || null,
        PermanentAddress: form.permanentAddress || null,
        EmergencyContactRelation: form.emergencyRelation || null,
        EmergencyContactPersonName: form.emergencyName || null,
        EmergencyContactNumber: form.emergencyPhone || null,
        TenantProfession: form.profession || null,
        PoliceVerification: null,
        CheckInDate: form.checkInDate,
        CheckOutDate: null,
        JoiningDate: form.checkInDate,
        ExitDate: null,
        RentType: null,
        RentAmount: null,
        BedID: null,
      }

      formData.append('insertDto', JSON.stringify(tenantData))

      if (form.aadhaarFile) {
        formData.append('aadhaar', form.aadhaarFile)
      }
      if (form.photoFile) {
        formData.append('photo', form.photoFile)
      }
      if (form.idFile) {
        formData.append('workingId', form.idFile)
      }

      const result = await insert_tenant_with_files(formData)

      if (result && (result.Success === 1 || result.statusCode === 201)) {
        if (onSubmit) {
          onSubmit({
            ...form,
            room,
            id: Date.now(),
            submittedAt: new Date().toISOString(),
            status: 'pending',
            tenantId: result.TenantID || result.data?.tenantId
          })
        }
        onSuccess()
        reset()
        onClose()
        alert(result.Message || 'Tenant added successfully!')
      } else if (result && result.statusCode === 413) {
        setApiError('File size exceeds the limit. Please upload smaller files.')
      } else {
        let errorMsg = result?.Message || result?.message || 'Failed to add tenant. Please try again.'
        if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
          errorMsg = 'This phone number is already registered. Please use a different phone number.'
        }
        setApiError(errorMsg)
      }
    } catch (error) {
      console.error('Error submitting tenant:', error)
      let errorMsg = error.message || 'Failed to add tenant. Please try again.'
      if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
        errorMsg = 'This phone number is already registered. Please use a different phone number.'
      }
      setApiError(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleSendLink = async () => {
    // Validate email
    if (!linkEmail.trim() || !/\S+@\S+\.\S+/.test(linkEmail)) {
      setApiError('Please enter a valid email address.')
      return
    }
    if (!selectedPG?.PGID) {
      setApiError('No PG selected.')
      return
    }

    setSendingLink(true)
    setApiError(null)

    try {
      const data = {
        PGID: selectedPG.PGID,
        email: linkEmail
      }
      const result = await sendTenantFormLink(data)

      if (result.success) {
        setLinkSent(true)
      } else {
        setApiError(result.message || 'Failed to send form link')
      }
    } catch (error) {
      console.error('Error sending link:', error)
      setApiError(error.message || 'Failed to send form link. Please try again.')
    } finally {
      setSendingLink(false)
    }
  }

  // ─── QR validation helpers ──────────────────────────────────────────
  const validateQrField = (field, value) => {
    switch (field) {
      case 'tenantName':
        if (!value.trim()) return 'Tenant name is required'
        if (value.trim().length < 2) return 'Name must be at least 2 characters'
        return ''
      case 'advanceAmount':
        if (!value.trim()) return 'Advance amount is required'
        const advance = parseFloat(value)
        if (isNaN(advance) || advance <= 0) return 'Enter a positive amount'
        if (advance > 100000) return 'Amount cannot exceed ₹1,00,000'
        return ''
      case 'securityDeposit':
        if (!value.trim()) return 'Security deposit is required'
        const security = parseFloat(value)
        if (isNaN(security) || security < 0) return 'Enter a valid amount'
        return ''
      case 'phone':
        const digits = value.replace(/\D/g, '')
        if (!digits) return 'Mobile number is required'
        if (digits.length !== 10) return 'Mobile number must be exactly 10 digits'
        return ''
      case 'email':
        if (!value.trim()) return 'Email is required'
        if (!/\S+@\S+\.\S+/.test(value)) return 'Enter a valid email address'
        return ''
      default:
        return ''
    }
  }

  // Update a QR field and re‑validate that field only
  const updateQrField = (field, value) => {
    let processedValue = value
    if (field === 'phone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10)
    }

    switch (field) {
      case 'tenantName': setQrTenantName(processedValue); break
      case 'advanceAmount': setQrAdvanceAmount(processedValue); break
      case 'securityDeposit': setQrSecurityDeposit(processedValue); break
      case 'phone': setQrPhone(processedValue); break
      case 'email': setQrEmail(processedValue); break
      case 'upiId': setQrUpiId(processedValue); break
      default: break
    }

    const error = validateQrField(field, processedValue)
    setQrErrors(prev => ({ ...prev, [field]: error }))
  }

 // Update isQrFormValid - Remove upiId from required fields
const isQrFormValid = () => {
  const fields = ['tenantName', 'advanceAmount', 'phone', 'email']
  return fields.every(f => {
    const error = qrErrors[f]
    return !error
  }) && qrTenantName.trim() && qrAdvanceAmount.trim() &&
    qrPhone.trim() && qrEmail.trim()
}

// Update generateQRPreview - Use default UPI if empty
const generateQRPreview = async () => {
  const errors = {}
  const fields = ['tenantName', 'advanceAmount', 'phone', 'email']
  fields.forEach(f => {
    let value
    switch (f) {
      case 'tenantName': value = qrTenantName; break
      case 'advanceAmount': value = qrAdvanceAmount; break
      case 'phone': value = qrPhone; break
      case 'email': value = qrEmail; break
      default: return
    }
    const err = validateQrField(f, value)
    if (err) errors[f] = err
  })

  if (Object.keys(errors).length) {
    setQrErrors(prev => ({ ...prev, ...errors }))
    return
  }

  try {
    // Use default UPI if user didn't enter one
    const finalUpiId = qrUpiId.trim() || '7838089760@kotak'
    
    const upiLink = `upi://pay?pa=${finalUpiId}&pn=PGRent&am=${qrAdvanceAmount}.00&cu=INR&tn=Advance Payment - ${qrTenantName}`;
console.log("upiLink", upiLink);

    if (qrCanvasRef.current) {
      await QRCode.toCanvas(qrCanvasRef.current, upiLink, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff"
        }
      });

      const url = qrCanvasRef.current.toDataURL("image/png");
      setQrImageUrl(url);
      setShowQRPreview(true);
    }
  } catch (error) {
    console.error("QR generation failed:", error);
    setApiError("Failed to generate QR code. Please try again.");
  }
};


  // Update handleSendQR - use qrUpiId
  const handleSendQR = async () => {
    const errors = {}
    const fields = ['tenantName', 'advanceAmount' , 'phone', 'email']
    fields.forEach(f => {
      let value
      switch (f) {
        case 'tenantName': value = qrTenantName; break
        case 'advanceAmount': value = qrAdvanceAmount; break
        case 'phone': value = qrPhone; break
        case 'email': value = qrEmail; break
        default: return
      }
      const err = validateQrField(f, value)
      if (err) errors[f] = err
    })

    if (Object.keys(errors).length) {
      setQrErrors(prev => ({ ...prev, ...errors }))
      return
    }

    if (!selectedPG?.PGID) {
      setApiError('No PG selected. Please select a PG from the dashboard.')
      return
    }

    if (!qrImageUrl) {
      setApiError('Please generate QR code first by clicking "Preview QR"')
      return
    }

    setSendingQR(true)
    setApiError(null)

    try {
    const finalUpiId = qrUpiId.trim() || '7838089760@kotak'
    
    const upiLink = `upi://pay?pa=${finalUpiId}&pn=PGRent&am=${qrAdvanceAmount}.00&cu=INR&tn=Advance Payment - ${qrTenantName}`;

    const requestData = {
      PGID: selectedPG.PGID,
      tenantName: qrTenantName.trim(),
      phoneNumber: qrPhone,
      advanceAmount: parseFloat(qrAdvanceAmount),
      email: qrEmail.trim(),
      securityDeposit: parseFloat(qrSecurityDeposit) || 0,
      qrImageBase64: qrImageUrl,
      qrCodeData: upiLink,
      upiId: finalUpiId
    }

    const result = await sendQRCode(requestData)

    if (result.success) {
      setGeneratedQRData(result.data)
      setQrSent(true)
    } else {
      setApiError(result.message || 'Failed to send QR code')
    }
    } catch (error) {
      console.error('Error sending QR code:', error)
      setApiError(error.message || 'Failed to send QR code. Please try again.')
    } finally {
      setSendingQR(false)
    }
  }

  return (
    <div className="atm-overlay" >
      <div className="atm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="atm-head">
          <div>
            <h2 className="atm-title">Add Tenant</h2>
            {room && (
              <p className="atm-sub">
                Room {room.id} · Floor {room.floor}
                {room.type && <span className="atm-sub-type">{room.type}</span>}
              </p>
            )}
            {selectedPG && (
              <p className="atm-sub" style={{ color: '#6366F1', fontSize: '12px', marginTop: '4px' }}>
                PG: {selectedPG.PGName}
              </p>
            )}
          </div>
          <button className="atm-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* API Error Display */}
        {apiError && (
          <div className="atm-error-box" style={{
            margin: '16px 24px',
            padding: '12px',
            background: '#FEF2F2',
            border: '1px solid #FEE2E2',
            borderRadius: '8px',
            color: '#DC2626',
            fontSize: '13px'
          }}>
            <strong>Error:</strong> {apiError}
          </div>
        )}

        {/* Mode picker */}
        {!mode && (
          <div className="atm-body">
            <p className="atm-mode-hint">How would you like to add this tenant?</p>
            <div className="atm-mode-cards">
              <button className="atm-mode-card" onClick={() => setMode('link')}>
                <div className="atm-mode-icon atm-mode-icon-link">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.5 19.79 19.79 0 01.4 0.84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <span className="atm-mode-label">Send Form Link</span>
                <span className="atm-mode-desc">Send a WhatsApp / SMS link to the tenant so they can fill the form themselves.</span>
              </button>

              <button className="atm-mode-card" onClick={() => setMode('qr')}>
                <div className="atm-mode-icon atm-mode-icon-qr">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="2" y="2" width="8" height="8" rx="1" />
                    <rect x="14" y="2" width="8" height="8" rx="1" />
                    <rect x="2" y="14" width="8" height="8" rx="1" />
                    <line x1="14" y1="14" x2="18" y2="14" />
                    <line x1="18" y1="18" x2="22" y2="18" />
                    <line x1="14" y1="18" x2="14" y2="22" />
                    <line x1="18" y1="14" x2="18" y2="18" />
                  </svg>
                </div>
                <span className="atm-mode-label">Send QR Code</span>
                <span className="atm-mode-desc">Send a QR code image via email &amp; SMS for the tenant to scan and fill the form.</span>
              </button>

              <button className="atm-mode-card" onClick={() => setMode('form')}>
                <div className="atm-mode-icon atm-mode-icon-form">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <span className="atm-mode-label">Fill Form Now</span>
                <span className="atm-mode-desc">Fill the tenant details directly. Best when tenant is present with you.</span>
              </button>
            </div>
          </div>
        )}

        {/* Send Link mode */}
        {mode === 'link' && (
          <div className="atm-body">
            <button className="atm-back" onClick={() => { setMode(null); setLinkSent(false); setLinkEmail('') }}>
              ← Back
            </button>
            {!linkSent ? (
              <div className="atm-link-panel">
                <div className="atm-info-box">
                  Enter the tenant's email address. They'll receive a message with a link to fill their details.
                </div>
                <div className="atm-field">
                  <label className="atm-label">Tenant's Email Address <span className="atm-req">*</span></label>
                  <input
                    className="atm-input"
                    type="email"
                    placeholder="Enter tenant's email address"
                    value={linkEmail}
                    onChange={e => setLinkEmail(e.target.value)}
                  />
                </div>
                <button
                  className="atm-btn atm-btn-primary atm-send-btn"
                  onClick={handleSendLink}
                  disabled={!linkEmail.trim() || sendingLink}>
                  {sendingLink ? (
                    <>
                      <span className="atm-spinner" style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        border: '2px solid #fff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                        marginRight: '8px'
                      }}></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      Send Link
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="atm-sent-panel">
                <div className="atm-sent-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="atm-sent-title">Link sent to {linkEmail}!</p>
                <p className="atm-sent-desc">
                  Once the tenant fills the form, they will appear in your <strong>Verify Tenants</strong> list on the dashboard.
                </p>
                <button className="atm-btn atm-btn-outline" onClick={handleClose}>Done</button>
              </div>
            )}
          </div>
        )}

        {/* ─── QR Code mode with real‑time validation ───────────── */}
        {mode === 'qr' && (
          <div className="atm-body">
            <button className="atm-back" onClick={() => {
              setMode(null);
              setQrSent(false);
              setQrTenantName('');
              setQrAdvanceAmount('');
              setQrSecurityDeposit('');
              setQrPhone('');
              setQrEmail('');
              setQrFile(null);
              setQrErrors({});
              setGeneratedQRData(null);
              setQrImageUrl('');
              setShowQRPreview(false);
            }}>
              ← Back
            </button>

            {!qrSent ? (
              <div className="atm-link-panel">
                <div className="atm-info-box">
                  Enter tenant details to generate QR code for advance payment.
                  The QR code will be sent via email.
                </div>

                <div className="atm-grid">
                  {/* Tenant Name - First field */}
                  <div className={`atm-field atm-field-full ${qrErrors.tenantName ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Tenant Name <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="text"
                      placeholder="Enter tenant's full name"
                      value={qrTenantName}
                      onChange={e => updateQrField('tenantName', e.target.value)}
                    />
                    {qrErrors.tenantName && <span className="atm-err-msg">{qrErrors.tenantName}</span>}
                  </div>

                  {/* Advance Amount - Second field */}
                  <div className={`atm-field ${qrErrors.advanceAmount ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Advance Amount (₹) <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="number"
                      placeholder="Enter advance amount"
                      value={qrAdvanceAmount}
                      onChange={e => updateQrField('advanceAmount', e.target.value)}
                      min="0"
                      step="100"
                    />
                    {qrErrors.advanceAmount && <span className="atm-err-msg">{qrErrors.advanceAmount}</span>}
                  </div>


                  {/* UPI ID */}
                  <div className={`atm-field ${qrErrors.upiId ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">UPI ID <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="text"
                      placeholder=""
                      value={qrUpiId}
                      onChange={e => updateQrField('upiId', e.target.value)}
                    />
                    {qrErrors.upiId && <span className="atm-err-msg">{qrErrors.upiId}</span>}
                    
                  </div>

                  {/* Security Deposit */}
                  {/* <div className={`atm-field ${qrErrors.securityDeposit ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Security Deposit (₹) <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="number"
                      placeholder="Enter security deposit"
                      value={qrSecurityDeposit}
                      onChange={e => updateQrField('securityDeposit', e.target.value)}
                      min="0"
                      step="100"
                    />
                    {qrErrors.securityDeposit && <span className="atm-err-msg">{qrErrors.securityDeposit}</span>}
                  </div> */}

                  {/* Mobile Number */}
                  <div className={`atm-field ${qrErrors.phone ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Mobile Number <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      value={qrPhone}
                      onChange={e => updateQrField('phone', e.target.value)}
                      maxLength="10"
                    />
                    {qrErrors.phone && <span className="atm-err-msg">{qrErrors.phone}</span>}
                  </div>

                  {/* Email */}
                  <div className={`atm-field ${qrErrors.email ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Email Address <span className="atm-req">*</span></label>
                    <input
                      className="atm-input"
                      type="email"
                      placeholder="Enter tenant's email address"
                      value={qrEmail}
                      onChange={e => updateQrField('email', e.target.value)}
                    />
                    {qrErrors.email && <span className="atm-err-msg">{qrErrors.email}</span>}
                  </div>
                </div>

                {/* QR Code Preview - Hidden canvas */}
                <canvas ref={qrCanvasRef} style={{ display: 'none' }} />

                {/* Generate Preview Button */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    className="atm-btn atm-btn-outline"
                    onClick={generateQRPreview}
                    disabled={!isQrFormValid()}
                    style={{ flex: 1 }}
                  >
                    👁️ Preview QR
                  </button>
                </div>

                {/* QR Code Display */}
                {showQRPreview && qrImageUrl && (
                  <div style={styles.qrPreviewContainer}>
                    <h4 style={{ margin: '15px 0 10px 0', textAlign: 'center' }}>
                      QR Code for ₹{qrAdvanceAmount}
                    </h4>
                    <div style={styles.qrPreviewBox}>
                      <img
                        src={qrImageUrl}
                        alt="QR Code Preview"
                        style={{ width: '200px', height: '200px' }}
                      />
                    </div>
                    <div style={styles.qrDetails}>
                      <p><strong>Tenant:</strong> {qrTenantName}</p>
                      <p><strong>Amount:</strong> ₹{qrAdvanceAmount}</p>
                     <p><strong>UPI:</strong> {qrUpiId}</p>
                    </div>
                  </div>
                )}

                {/* QR Code Image Upload (optional) */}
                {/* <FileInput
                  label="QR Code Image (optional - upload custom)"
                  accept="image/*"
                  value={qrFile}
                  onChange={(file) => setQrFile(file)}
                  fieldName="qrFile"
                  hint="Upload a custom QR code image (PNG, JPG)"
                /> */}

                <button
                  className="atm-btn atm-btn-primary atm-send-btn"
                  onClick={handleSendQR}
                  disabled={!isQrFormValid() || sendingQR}
                  style={{ marginTop: '16px' }}
                >
                  {sendingQR ? (
                    <>
                      <span className="atm-spinner" style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        border: '2px solid #fff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                        marginRight: '8px'
                      }}></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      Generate & Send QR Code
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="atm-sent-panel">
                <div className="atm-sent-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="atm-sent-title">QR Code Sent Successfully!</p>

                {generatedQRData && (
                  <div className="atm-qr-details" style={{
                    textAlign: 'left',
                    background: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '8px',
                    margin: '15px 0'
                  }}>
                    <p><strong>Tenant:</strong> {qrTenantName}</p>
                    <p><strong>Advance Amount:</strong> ₹{qrAdvanceAmount}</p>
                    {/* <p><strong>Security Deposit:</strong> ₹{qrSecurityDeposit}</p> */}
                    <p><strong>Mobile:</strong> {qrPhone}</p>
                    <p><strong>Email:</strong> {qrEmail}</p>
                    <p><strong>UPI ID:</strong> {qrUpiId|| '7838089760@kotak'}</p>
                    <p><strong>QR Request ID:</strong> {generatedQRData?.qrRequestId || 'N/A'}</p>
                  </div>
                )}

                {/* Show QR Code again in success state */}
                {qrImageUrl && (
                  <div style={styles.qrPreviewContainer}>
                    <div style={styles.qrPreviewBox}>
                      <img
                        src={qrImageUrl}
                        alt="QR Code"
                        style={{ width: '200px', height: '200px' }}
                      />
                    </div>
                  </div>
                )}

                <p className="atm-sent-desc" style={{ marginBottom: '15px' }}>
                  QR code has been sent to <strong>{qrEmail}</strong>.
                  The tenant can scan the QR code to make the advance payment.
                </p>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button className="atm-btn atm-btn-primary" onClick={handleClose}>Done</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fill Form mode (unchanged) */}
        {mode === 'form' && (
          <>
            <div className="atm-form-body">
              <button className="atm-back" onClick={() => setMode(null)}>← Back</button>

              {/* Personal Information */}
              <div className="atm-section">
                <h4 className="atm-section-title">Personal Information</h4>
                <div className="atm-grid">

                  <div className={`atm-field atm-field-full ${errors.name ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Full Name <span className="atm-req">*</span></label>
                    <input className="atm-input" placeholder="e.g. Rahul Sharma"
                      value={form.name} onChange={e => set('name', e.target.value)} />
                    {errors.name && <span className="atm-err-msg">{errors.name}</span>}
                  </div>

                  <div className="atm-field">
                    <label className="atm-label">Age</label>
                    <input className="atm-input" type="number" placeholder="e.g. 24" min="16" max="80"
                      value={form.age} onChange={e => set('age', e.target.value)} />
                  </div>

                  <div className="atm-field">
                    <label className="atm-label">Date of Birth</label>
                    <input className="atm-input" type="date"
                      value={form.dob} onChange={e => set('dob', e.target.value)} />
                  </div>

                  <div className="atm-field">
                    <label className="atm-label">Gender</label>
                    <select className="atm-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className={`atm-field ${errors.phone ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Mobile Number <span className="atm-req">*</span></label>
                    <input className="atm-input" type="tel" placeholder="+91 98765 43210"
                      value={form.phone} onChange={e => set('phone', e.target.value)} maxLength="10"/>
                    {errors.phone && <span className="atm-err-msg">{errors.phone}</span>}
                  </div>

                  <div className={`atm-field ${errors.email ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Email <span className="atm-req">*</span></label>
                    <input className="atm-input" type="email" placeholder="e.g. rahul@email.com"
                      value={form.email} onChange={e => set('email', e.target.value)} />
                    {errors.email && <span className="atm-err-msg">{errors.email}</span>}
                  </div>

                  <div className="atm-field atm-field-full">
                    <label className="atm-label">Present Address</label>
                    <textarea className="atm-textarea" rows={2}
                      placeholder="House No., Street, City, State, PIN…"
                      value={form.presentAddress} onChange={e => set('presentAddress', e.target.value)} />
                  </div>

                  <div className="atm-field atm-field-full">
                    <label className="atm-label">Permanent Address</label>
                    <textarea className="atm-textarea" rows={2}
                      placeholder="House No., Street, City, State, PIN…"
                      value={form.permanentAddress} onChange={e => set('permanentAddress', e.target.value)} />
                  </div>

                </div>
              </div>

              {/* Emergency Contact */}
              <div className="atm-section">
                <h4 className="atm-section-title">Emergency Contact</h4>
                <div className="atm-grid">

                  <div className="atm-field">
                    <label className="atm-label">Contact Name</label>
                    <input className="atm-input" placeholder="e.g. Suresh Sharma"
                      value={form.emergencyName} onChange={e => set('emergencyName', e.target.value)} />
                  </div>

                  <div className="atm-field">
                    <label className="atm-label">Contact Number</label>
                    <input className="atm-input" type="tel" placeholder="+91 98765 43210"
                      value={form.emergencyPhone} onChange={e => set('emergencyPhone', e.target.value)} />
                  </div>

                  <div className="atm-field">
                    <label className="atm-label">Relation</label>
                    <select className="atm-select" value={form.emergencyRelation} onChange={e => set('emergencyRelation', e.target.value)}>
                      <option value="">Select relation</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="spouse">Spouse</option>
                      <option value="friend">Friend</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                </div>
              </div>

              {/* Professional Details */}
              <div className="atm-section">
                <h4 className="atm-section-title">Professional Details</h4>
                <div className="atm-grid">

                  <div className="atm-field">
                    <label className="atm-label">Profession</label>
                    <select className="atm-select" value={form.profession} onChange={e => set('profession', e.target.value)}>
                      <option value="">Select profession</option>
                      <option value="student">Student</option>
                      <option value="working">Working Professional</option>
                      <option value="business">Business / Self-employed</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className={`atm-field ${errors.checkInDate ? 'atm-field-err' : ''}`}>
                    <label className="atm-label">Check-in Date <span className="atm-req">*</span></label>
                    <input className="atm-input" type="date"
                      value={form.checkInDate} onChange={e => set('checkInDate', e.target.value)} />
                    {errors.checkInDate && <span className="atm-err-msg">{errors.checkInDate}</span>}
                  </div>

                </div>
              </div>

              {/* Documents */}
              <div className="atm-section">
                <h4 className="atm-section-title">Documents</h4>
                <div className="atm-grid atm-grid-3">
                  <FileInput
                    label="Aadhaar Card"
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={form.aadhaarFile}
                    onChange={setFile}
                    fieldName="aadhaarFile"
                    hint="PDF or image (Max 5MB)"
                  />
                  <FileInput
                    label="Passport Photo"
                    accept=".jpg,.jpeg,.png"
                    value={form.photoFile}
                    onChange={setFile}
                    fieldName="photoFile"
                    hint="JPG / PNG (Max 5MB)"
                  />
                  <FileInput
                    label="Work / Student ID"
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={form.idFile}
                    onChange={setFile}
                    fieldName="idFile"
                    hint="PDF or image (Max 5MB)"
                  />
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="atm-footer">
              <button className="atm-btn atm-btn-outline" onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button className="atm-btn atm-btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <span className="atm-spinner" style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      border: '2px solid #fff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite',
                      marginRight: '8px'
                    }}></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Submit
                  </>
                )}
              </button>
            </div>
          </>
        )}

      </div>
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
          .atm-spinner {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 2px solid #fff;
            border-top-color: transparent;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
            margin-right: 8px;
          }
      `}</style>
    </div>
  )
}

const styles = {
  qrPreviewContainer: {
    marginTop: '15px',
    textAlign: 'center'
  },
  qrPreviewBox: {
    display: 'inline-block',
    padding: '15px',
    background: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  qrDetails: {
    textAlign: 'left',
    marginTop: '10px',
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '5px',
    fontSize: '13px'
  }
};

export default AddTenantModal